import type { StripeErrorResponse } from "store/billing/types"
import { type ProductType } from "store/product/types"
import type {
  GlassesDraftListItem,
  GlassesEditorDto,
  GlassesEditorInitialSku,
  GlassesLimits,
  GlassesDigitizationSummary,
} from "types"
import { getPlatformHeaders } from "./platformApiAuth"
import { isBillingPaywallError, syncBillingDenyState } from "./platformBillingState"
import { getPlatformServiceEndpoint } from "./platformServiceEndpoints"
import { GENERIC_USER_ERROR_MESSAGE } from "utils/userFacingErrors"

type AdminProductsResponse = {
  items: unknown[]
  pageCount: number
}

type AdminProductTranslationsResponse = {
  alternateName: Array<{ code: string; name: string }>
  alternateSKUsName: Array<{
    skuId: string
    skuI8N: Array<{ code: string; name: string }>
  }>
}

type AdminProductTypesResponse = {
  items: ProductType[]
}

type AdminUploadUrlResponse = {
  contentType: string
  expiresIn: number
  key: string
  method: string
  uploadUrl: string
}

type StartGlassesDigitizationResponse = {
  digitization: GlassesDigitizationSummary
  externalId: string
  productId: string
}

export type IntegrationQrCodeTarget =
  | {
      merchantId: string
      type: "merchant"
    }
  | {
      activeSkuId?: string
      merchantId: string
      productId: string
      skuIds: string[]
      type: "product"
    }

export type IntegrationQrCodeResponse = {
  downloads: {
    htmlSnippet: string
    png: {
      dataUrl: string
      filename: string
    }
    webp: {
      dataUrl: string
      filename: string
    }
  }
  shortCode: string
  shortLink: string
  target: IntegrationQrCodeTarget
  targetUrl: string
}

export type AdminProductDetailsForQrCode = {
  id?: string | null
  name?: string | null
  skus?: Array<{
    id?: string | null
    name?: string | null
    sku?: string | null
  }> | null
}

const ADMIN_TRANSPORT_FAILURE_COOLDOWN_MS = 30_000

type AdminTransportFailureState = {
  retryAfter: number
}

let adminTransportFailureState: AdminTransportFailureState | null = null

const readActiveTransportFailure = (): AdminTransportFailureState | null => {
  if (!adminTransportFailureState) return null

  if (adminTransportFailureState.retryAfter <= Date.now()) {
    adminTransportFailureState = null
    return null
  }

  return adminTransportFailureState
}

const rememberTransportFailure = (): void => {
  adminTransportFailureState = {
    retryAfter: Date.now() + ADMIN_TRANSPORT_FAILURE_COOLDOWN_MS,
  }
}

const clearTransportFailure = (): void => {
  adminTransportFailureState = null
}

class AdminRequestError extends Error {
  data: StripeErrorResponse | null
  status: number

  constructor(message: string, status: number, data: StripeErrorResponse | null) {
    super(message)
    this.name = "AdminRequestError"
    this.data = data
    this.status = status
  }
}

class AdminApi {
  private readonly adminEndpoint = getPlatformServiceEndpoint("admin")

  private async parseAdminError(response: Response): Promise<StripeErrorResponse | null> {
    try {
      return (await response.json()) as StripeErrorResponse
    } catch {
      return null
    }
  }

  private async requestAdminJson<T>(
    path: string,
    options: {
      body?: string
      contentType?: string
      defaultErrorMessage: string
      method: "DELETE" | "GET" | "PATCH" | "POST" | "PUT"
      suppressPaywall?: boolean
    }
  ): Promise<T> {
    const activeTransportFailure = readActiveTransportFailure()
    if (activeTransportFailure) throw new AdminRequestError(GENERIC_USER_ERROR_MESSAGE, 0, null)

    let response: Response

    try {
      response = await fetch(`${this.adminEndpoint}${path}`, {
        method: options.method,
        headers: await getPlatformHeaders(options.contentType),
        ...(options.body ? { body: options.body } : {}),
      })
    } catch {
      rememberTransportFailure()

      throw new AdminRequestError(GENERIC_USER_ERROR_MESSAGE, 0, null)
    }

    clearTransportFailure()

    if (!response.ok) {
      const data = await this.parseAdminError(response)

      if (!options.suppressPaywall && isBillingPaywallError(data)) syncBillingDenyState(data)

      throw new AdminRequestError(GENERIC_USER_ERROR_MESSAGE, response.status, data)
    }

    return (await response.json()) as T
  }

  async listProducts(params: {
    category: string
    limit?: number
    merchantId?: string
    page?: number
    partial?: string
    suppressPaywall?: boolean
  }): Promise<AdminProductsResponse> {
    const query = new URLSearchParams()
    query.set("category", params.category)
    if (params.limit !== undefined) query.set("limit", String(params.limit))
    if (params.merchantId) query.set("merchantId", params.merchantId)
    if (params.page !== undefined) query.set("page", String(params.page))
    if (params.partial) query.set("partial", params.partial)

    return this.requestAdminJson<AdminProductsResponse>(`/products?${query.toString()}`, {
      defaultErrorMessage: "Failed to load products",
      method: "GET",
      suppressPaywall: params.suppressPaywall,
    })
  }

  async listProductTypes(options?: {
    suppressPaywall?: boolean
  }): Promise<AdminProductTypesResponse> {
    return this.requestAdminJson<AdminProductTypesResponse>("/product-types", {
      defaultErrorMessage: "Failed to load product types",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getProduct(productId: string, options?: { suppressPaywall?: boolean }) {
    return this.requestAdminJson<AdminProductDetailsForQrCode>(
      `/products/${encodeURIComponent(productId)}`,
      {
        defaultErrorMessage: "Failed to load product",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async generateIntegrationQrCode(input: {
    merchantId: string
    productId?: string
    skuId?: string
  }): Promise<IntegrationQrCodeResponse> {
    return this.requestAdminJson<IntegrationQrCodeResponse>("/integrations/qr-codes", {
      body: JSON.stringify(input),
      contentType: "application/json",
      defaultErrorMessage: "Failed to generate QR code",
      method: "POST",
    })
  }

  async saveProduct(
    payload: unknown,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<{ id: string }> {
    return this.requestAdminJson<{ id: string }>("/products", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to save product",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async authorizeCdn(options?: { suppressPaywall?: boolean }): Promise<{ jwt: string }> {
    return this.requestAdminJson<{ jwt: string }>("/cdn-token", {
      defaultErrorMessage: "Failed to authorize CDN upload",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async toggleProductVisibility(
    productId: string,
    visible: boolean,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<{ success: boolean }> {
    return this.requestAdminJson<{ success: boolean }>(
      `/products/${encodeURIComponent(productId)}/visibility`,
      {
        body: JSON.stringify({ visible }),
        contentType: "application/json",
        defaultErrorMessage: "Failed to update product visibility",
        method: "PATCH",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async deleteProduct(productId: string, options?: { suppressPaywall?: boolean }) {
    return this.requestAdminJson<{ success: boolean }>(
      `/products/${encodeURIComponent(productId)}`,
      {
        defaultErrorMessage: "Failed to delete product",
        method: "DELETE",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async getGlassesEditor(
    productId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/products/${encodeURIComponent(productId)}`,
      {
        defaultErrorMessage: "Couldn't open this item",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async createGlassesDraft(
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>("/glasses/drafts", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Couldn't save",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getGlassesDraft(
    draftId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/drafts/${encodeURIComponent(draftId)}`,
      {
        defaultErrorMessage: "Couldn't open this item",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async listGlassesDrafts(
    params: {
      merchantId: string
      partial?: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesDraftListItem[]> {
    const query = new URLSearchParams()
    query.set("merchantId", params.merchantId)
    if (params.partial?.trim()) query.set("partial", params.partial.trim())

    return this.requestAdminJson<GlassesDraftListItem[]>(`/glasses/drafts?${query.toString()}`, {
      defaultErrorMessage: "Couldn't load drafts",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getGlassesLimits(
    params: {
      merchantId: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesLimits> {
    const query = new URLSearchParams()
    query.set("merchantId", params.merchantId)

    return this.requestAdminJson<GlassesLimits>(`/glasses/limits?${query.toString()}`, {
      defaultErrorMessage: "Couldn't load limits",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async saveGlassesDraft(
    draftId: string,
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/drafts/${encodeURIComponent(draftId)}`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Couldn't save",
        method: "PUT",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async deleteGlassesDraft(
    draftId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<{ success: true }> {
    return this.requestAdminJson<{ success: true }>(
      `/glasses/drafts/${encodeURIComponent(draftId)}`,
      {
        defaultErrorMessage: "Couldn't cancel draft",
        method: "DELETE",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async finalizeGlassesDraft(
    draftId: string,
    payload: {
      cover: string
      effectUrl?: string | null
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
      visible: boolean
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/drafts/${encodeURIComponent(draftId)}/finalize`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Couldn't save",
        method: "POST",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async saveGlassesEditor(
    productId: string,
    payload: {
      cover: string
      effectUrl?: string | null
      externalId?: string | null
      name: string
      url?: string | null
      visible: boolean
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/products/${encodeURIComponent(productId)}`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Couldn't save",
        method: "PUT",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async createGlassesSourceImageUploadUrl(
    payload: {
      contentType: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<AdminUploadUrlResponse> {
    return this.requestAdminJson<AdminUploadUrlResponse>("/glasses/source-images/upload-url", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Couldn't upload photo",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async startGlassesDraftDigitization(
    draftId: string,
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      sourceImageKeys: string[]
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/drafts/${encodeURIComponent(draftId)}/digitization-jobs`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Couldn't start",
        method: "POST",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async startInitialGlassesDraftDigitization(
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      sourceImageKeys: string[]
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>("/glasses/draft-digitization-jobs", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Couldn't start",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async startGlassesDigitization(
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      productId?: string | null
      sourceImageKeys: string[]
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<StartGlassesDigitizationResponse> {
    return this.requestAdminJson<StartGlassesDigitizationResponse>("/glasses/digitization-jobs", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Couldn't start",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getAnalytics(params: {
    merchantId: string
    days?: string
    dateStart?: string
    dateEnd?: string
    suppressPaywall?: boolean
  }) {
    const query = new URLSearchParams()
    query.set("merchantId", params.merchantId)
    if (params.days) query.set("days", params.days)
    if (params.dateStart) query.set("dateStart", params.dateStart)
    if (params.dateEnd) query.set("dateEnd", params.dateEnd)

    return this.requestAdminJson<{ analytics: unknown }>(`/analytics?${query.toString()}`, {
      defaultErrorMessage: "Failed to load analytics",
      method: "GET",
      suppressPaywall: params.suppressPaywall,
    })
  }

  async getUiCustomization(
    merchantId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ) {
    const query = new URLSearchParams({ merchantId })
    return this.requestAdminJson<{ styles: string }>(`/ui-customization?${query.toString()}`, {
      defaultErrorMessage: "Failed to load UI customization",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async saveUiCustomization(
    payload: {
      data: string
      mainLogo: string
      merchantId: string
      shortLogo: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ) {
    return this.requestAdminJson<{ success: boolean }>("/ui-customization", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to save UI customization",
      method: "PUT",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getProductTranslations(
    productId: string,
    skuIds: string[],
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<AdminProductTranslationsResponse> {
    const query = new URLSearchParams({
      skuIds: skuIds.join(","),
    })

    return this.requestAdminJson<AdminProductTranslationsResponse>(
      `/products/${encodeURIComponent(productId)}/translations?${query.toString()}`,
      {
        defaultErrorMessage: "Failed to load product translations",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async saveProductTranslations(
    productId: string,
    payload: {
      data: string
      merchantId: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ) {
    return this.requestAdminJson<{ success: boolean }>(
      `/products/${encodeURIComponent(productId)}/translations`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Failed to save product translations",
        method: "POST",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async saveSkuTranslations(
    skuId: string,
    payload: {
      data: string
      merchantId: string
      productId: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ) {
    return this.requestAdminJson<{ success: boolean }>(
      `/skus/${encodeURIComponent(skuId)}/translations`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Failed to save SKU translations",
        method: "POST",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }
}

export const adminApi = new AdminApi()
